import { useState } from 'react'
import styled from 'styled-components'
import { mediaDesktop, mediaMobile, mediaTablet, mediaTabletAndMobile } from './styles/breakPoints'
import { Button } from '@favordelivery/tupperware'
import { CloseIcon, MenuIcon } from './Icons'
import { WELCOME_BACK_URL } from '@helpers/urls'
import { t, tStr } from '../utils/lang'

const Header = styled.header`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2;
  background-color: var(--themeColorBetaBgBrown);
  width: 100%;
  ${mediaDesktop} {
    height: 80px;
    overflow: hidden;
  }
  ${mediaMobile} {
    position: absolute;
  }
`

const Logo = styled.div`
  > svg {
    width: 150px;
    display: block;
    ${mediaDesktop} {
      margin-right: 48px;
    }
  }
`

const NavLink = styled.a`
  font-weight: ${p => p.theme.font.weight.normal};
  color: var(--themeColorForegroundSecondary);
  margin-right: 32px;
`
const SignInLink = styled.a`
  font-weight: ${p => p.theme.font.weight.light};
  color: var(--themeColorForegroundPrimary);
`
const SignUpButton = styled(Button)``

const Nav = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 16px min(112px, 10%);
  font-size: ${p => p.theme.font.size.title};
  svg {
    display: block;
  }
  ${SignInLink} {
    margin: 0 24px 0 auto;
  }
  ${SignUpButton} {
    width: 150px;
  }

  ${mediaTabletAndMobile} {
    ${NavLink} {
      display: none;
    }
  }
  ${mediaTablet} {
    padding: 16px 80px;
  }
  ${mediaMobile} {
    padding: 16px 24px;
    justify-content: space-between;
    ${SignInLink}, ${SignUpButton} {
      display: none;
    }
  }
`

const MenuLink = styled(Button)`
  padding: 12px;
  margin-left: 24px;
  ${mediaDesktop} {
    display: none;
  }
`

const NavMenu = styled.div<{ $show: boolean }>`
  height: calc(100vh - 80px);
  max-height: ${p => (p.$show ? '100vh' : '0')};
  overflow: hidden;
  transition: max-height 0.2s ease-out;
  > div {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    gap: 32px;
    padding: 64px 80px;
    height: 100%;
    font-size: ${p => p.theme.font.size.h3};
    ${mediaTablet} {
      ${SignInLink}, ${SignUpButton} {
        display: none;
      }
    }
    ${mediaMobile} {
      font-size: ${p => p.theme.font.size.h4};
      gap: 24px;
      padding: 64px 24px 32px;
    }
  }
`
const BottomLinks = styled.div`
  position: absolute;
  bottom: 32px;
  text-align: center;
  width: 100%;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 0 24px;
`

const HelpLinks = () => (
  <>
    <NavLink href="https://favor-runners.zendesk.com/hc/en-us">{t('navHelpCenter_s')}</NavLink>
    <NavLink href="https://rundown.favordelivery.com">{t('navBlog_s')}</NavLink>
    <NavLink href="https://runnerstore.favordelivery.com">{t('navStore_s')}</NavLink>
  </>
)

const SignUpLinks = () => (
  <>
    <SignInLink href={WELCOME_BACK_URL}>{t('navSignIn_s')}</SignInLink>
    <a href="/">
      <SignUpButton>{t('navSignUp_s')}</SignUpButton>
    </a>
  </>
)

const FavorLogo = () => (
  <svg aria-label="Favor" width="150" height="26" viewBox="0 0 150 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4284_4590)">
      <path
        d="M147.679 3.74013C147.906 3.74013 148.064 3.63522 148.064 3.45174V3.44287C148.064 3.27296 147.924 3.16349 147.692 3.16349H147.216V3.74013H147.679ZM146.976 2.94501H147.701C147.906 2.94501 148.077 3.01085 148.181 3.11552C148.26 3.18975 148.313 3.29887 148.313 3.43005V3.43867C148.313 3.70068 148.138 3.8579 147.893 3.91471L148.395 4.5655H148.098L147.631 3.95395H147.216V4.5655H146.976V2.94501ZM149.033 3.80143V3.7924C149.033 3.00208 148.417 2.35552 147.6 2.35552C146.779 2.35552 146.16 3.01085 146.16 3.80143V3.8098C146.16 4.60025 146.775 5.24671 147.592 5.24671C148.413 5.24671 149.033 4.59138 149.033 3.80143ZM146.016 3.8098V3.80143C146.016 2.94094 146.714 2.22052 147.6 2.22052C148.487 2.22052 149.177 2.93229 149.177 3.7924V3.80143C149.177 4.66155 148.478 5.38184 147.592 5.38184C146.705 5.38184 146.016 4.67029 146.016 3.8098Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M51.8926 2.38394H66.3302V7.29768H57.3519V11.0586H65.4199V15.7603H57.3519V23.6157H51.8926V2.38394Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M78.5235 15.473L76.5514 9.29028L74.5805 15.473H78.5235ZM73.8526 2.38394H79.3722L86.8645 23.6157H81.1319L79.8886 19.8161H73.2154L72.0024 23.6157H66.3906L73.8526 2.38394Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M84.9766 2.38394H90.9213L94.834 15.5334L98.7166 2.38394H104.54L97.3818 23.6157H92.1346L84.9766 2.38394Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M119.472 13.1821V12.8481C119.472 9.1783 117.712 6.93382 115.195 6.93382C112.677 6.93382 110.948 9.14797 110.948 12.8178V13.1515C110.948 16.8216 112.708 19.0662 115.225 19.0662C117.773 19.0662 119.472 16.8518 119.472 13.1821ZM105.367 13.2425V12.7877C105.367 6.44866 109.523 1.9895 115.225 1.9895C120.958 1.9895 125.052 6.38774 125.052 12.7574V13.2119C125.052 19.5514 120.897 24.0103 115.195 24.0103C109.462 24.0103 105.367 19.612 105.367 13.2425Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M136.107 12.6965C137.957 12.6965 139.05 11.6353 139.05 9.93674V9.87582C139.05 8.05584 137.897 7.14592 136.077 7.14592H133.407V12.6965H136.107ZM127.979 2.38394H136.32C139.171 2.38394 141.324 3.14211 142.689 4.53732C143.872 5.72024 144.539 7.3586 144.539 9.51198V9.66349C144.539 12.8178 142.992 14.7893 140.748 15.9119L145.116 23.6157H138.959L135.349 17.0339H133.407V23.6157H127.979V2.38394Z"
        fill="var(--themeColorBluePrimary)"
      />
      <path
        d="M36.9348 21.9996C34.79 21.5615 29.9512 20.3236 26.8818 17.6403V14.7484H32.5641C33.53 14.7484 34.3128 13.9658 34.3128 13C34.3128 12.0345 33.53 11.2518 32.5641 11.2518H26.8818V8.36004C29.9543 5.67402 34.7995 4.43637 36.9398 3.99965C37.7978 5.47267 39.3393 8.71782 39.3393 13C39.3393 17.2465 37.7908 20.5162 36.9348 21.9996ZM3.49681 13C3.49681 8.75285 5.0464 5.48217 5.90219 3.99927C8.04447 4.43416 12.8763 5.6665 15.9543 8.36026V11.2518H10.272C9.30642 11.2518 8.52348 12.0345 8.52348 13C8.52348 13.9658 9.30642 14.7484 10.272 14.7484H15.9543V17.64C12.8825 20.3261 8.03708 21.564 5.89566 22.0008C5.03864 20.5277 3.49681 17.2825 3.49681 13ZM39.1692 1.01648C38.786 0.511711 38.1598 0.252794 37.5339 0.340952C37.1965 0.387918 29.7356 1.47157 24.8961 5.45837C24.0586 5.07639 22.8641 4.69516 21.4182 4.69516C19.9718 4.69516 18.7774 5.07639 17.9399 5.45837C13.1002 1.47157 5.63971 0.387918 5.30261 0.340952C4.67591 0.254276 4.05006 0.511837 3.66734 1.01648C3.51751 1.21401 0 5.93713 0 13C0 20.0632 3.51742 24.7861 3.66722 24.9838C4.00034 25.4234 4.51796 25.6763 5.06032 25.6763C5.14072 25.6763 5.22161 25.6706 5.30248 25.6593C5.63958 25.6124 13.1004 24.5286 17.9401 20.5422C18.7775 20.924 19.972 21.3051 21.4182 21.3051C22.8641 21.3051 24.0586 20.924 24.896 20.5422C29.7355 24.5286 37.1965 25.6124 37.5339 25.6593C37.6144 25.6706 37.6952 25.6763 37.7757 25.6763C38.3179 25.6763 38.8356 25.4234 39.1692 24.9838C39.3185 24.7861 42.8363 20.0632 42.8363 13C42.8363 5.93713 39.3185 1.21401 39.1692 1.01648Z"
        fill="var(--themeColorBluePrimary)"
      />
    </g>
    <defs>
      <clipPath id="clip0_4284_4590">
        <rect width="150" height="25.3521" fill="white" transform="translate(0 0.323975)" />
      </clipPath>
    </defs>
  </svg>
)

const SiteHeader = () => {
  const [showMenu, setShowMenu] = useState(false)
  return (
    <Header>
      <Nav>
        <Logo>
          <FavorLogo />
        </Logo>
        <HelpLinks />
        <SignUpLinks />
        <MenuLink displayType="plain" onClick={() => setShowMenu(!showMenu)}>
          {!showMenu ? <MenuIcon alt={tStr('navOpenMenu_s')} /> : <CloseIcon alt={tStr('navCloseMenu_s')} />}
        </MenuLink>
      </Nav>
      <NavMenu $show={showMenu}>
        <div>
          <HelpLinks />
          <BottomLinks>
            <SignUpLinks />
          </BottomLinks>
        </div>
      </NavMenu>
    </Header>
  )
}

export default SiteHeader
